import React, { useState, useEffect } from 'react';
import { useSidebar } from '../../contexts/SidebarContext';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';

// ─── Assign/Edit Package Modal ────────────────────────────────────────────────
const AssignPackageModal = ({ isOpen, onClose, client, onSuccess }) => {
  const [packages, setPackages] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState(client?.package_id || '');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && client?.countries?.id) {
      fetchPackages(client.countries.id);
      setSelectedPackageId(client?.package_id || '');
    }
  }, [isOpen, client]);

  const fetchPackages = async (countryId) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('commercial_packages')
        .select(`*, countries ( id, currency_code )`)
        .eq('country_id', countryId)
        .eq('status', 'active')
        .order('tier', { ascending: true })
        .order('base_price', { ascending: true });
      if (error) throw error;
      setPackages(data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des packages');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedPackageId) { toast.error('Veuillez sélectionner un package'); return; }
    try {
      setSaving(true);
      const pkg = packages.find(p => p.id === selectedPackageId);
      if (!pkg) throw new Error('Package introuvable');
      const { error } = await supabase
        .from('user_profiles')
        .update({
          package_id: selectedPackageId,
          package_pay: 'pending',
          case_number: pkg.included_units.toString(),
          current_case_number: pkg.included_units.toString(),
        })
        .eq('id', client.id);
      if (error) throw error;
      toast.success('Package assigné avec succès !');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error('Erreur lors de l\'assignation du package', { description: error?.message });
    } finally {
      setSaving(false);
    }
  };

  const formatPrice = (price, currencyCode = 'EUR') =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currencyCode }).format(price);

  const getTierColor = (tier) => {
    switch (tier) {
      case 'bronze': return 'bg-amber-700 text-white';
      case 'silver': return 'bg-gray-400 text-gray-900';
      case 'gold': return 'bg-yellow-500 text-gray-900';
      case 'platinum': return 'bg-purple-600 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
      <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col animate-slide-in">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              {client?.package_id ? 'Modifier le package' : 'Assigner un package'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {client?.full_name} — {client?.countries?.country_name}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={20} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Icon name="Loader" size={24} className="animate-spin text-primary mr-3" />
              <span className="text-muted-foreground">Chargement des packages...</span>
            </div>
          ) : packages.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="PackageX" size={40} className="text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">Aucun package disponible pour ce pays.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {packages.map((pkg) => {
                const isSelected = selectedPackageId === pkg.id;
                const currencyCode = pkg?.countries?.currency_code || 'EUR';
                return (
                  <div
                    key={pkg.id}
                    onClick={() => setSelectedPackageId(pkg.id)}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all duration-150 ${
                      isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40 hover:bg-muted/30'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'border-primary' : 'border-muted-foreground'
                        }`}>
                          {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-foreground">{pkg.package_name}</span>
                            <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${getTierColor(pkg.tier)}`}>{pkg.tier}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{pkg.package_code}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{pkg.included_units} unités incluses</p>
                        </div>
                      </div>
                      <div className="text-right">
                        {pkg.discount_percentage > 0 && (
                          <p className="text-xs text-muted-foreground line-through">{formatPrice(pkg.base_price, currencyCode)}</p>
                        )}
                        <p className="text-lg font-bold text-foreground">{formatPrice(pkg.final_price, currencyCode)}</p>
                        {pkg.discount_percentage > 0 && (
                          <span className="text-xs bg-green-500 text-white px-1.5 py-0.5 rounded">-{pkg.discount_percentage}%</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 p-6 border-t border-border">
          <Button variant="default" fullWidth onClick={handleSave} loading={saving} disabled={!selectedPackageId || loading} iconName="Check" iconPosition="left" iconSize={18}>
            {client?.package_id ? 'Modifier le package' : 'Assigner le package'}
          </Button>
          <Button variant="outline" fullWidth onClick={onClose} disabled={saving}>Annuler</Button>
        </div>
      </div>
    </div>
  );
};

const BillingPhasesReadOnly = ({ packageId, currencyCode }) => {
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (packageId) fetchPhases();
  }, [packageId]);

const fetchPhases = async () => {
  try {
    setLoading(true);
    const { data, error } = await supabase
      .from('package_billing_phases')
      .select('*')
      .eq('package_id', packageId)
      .order('phase_number', { ascending: true });

console.log('packageId:', packageId);   // ← ADD
    console.log('data:', data);             // ← ADD  
    console.log('error:', error);           // ← ADD

    if (error) throw error;
    setPhases(data || []);
  } catch (err) {
    console.error('Error fetching phases:', err);
  } finally {
    setLoading(false);
  }
};
  const formatCurrency = (val) => {
    try {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: currencyCode || 'EUR',
      }).format(val ?? 0);
    } catch {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
      }).format(val ?? 0);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Icon name="Loader" size={18} className="animate-spin text-primary mr-2" />
        <span className="text-sm text-muted-foreground">Chargement des phases...</span>
      </div>
    );
  }

  if (phases.length === 0) {
    return (
      <div className="mt-4 flex items-center gap-3 p-4 bg-muted/30 rounded-lg">
        <Icon name="Info" size={16} className="text-muted-foreground flex-shrink-0" />
        <p className="text-sm text-muted-foreground">Aucune phase de facturation configurée.</p>
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-3">
      {phases.map((phase, index) => (
        <div key={phase.id} className="bg-muted/30 border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-primary">{index + 1}</span>
              </div>
              <span className="text-sm font-semibold text-foreground">{phase.phase_name}</span>
            </div>
            <span className="text-sm font-bold text-primary">
              {formatCurrency(phase.phase_price)}
            </span>
          </div>
          <div className="flex items-center gap-2 ml-8">
            <Icon name="ArrowRight" size={13} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">
              De <strong className="text-foreground">{phase.aligner_range_from}</strong>
              {' '}à <strong className="text-foreground">{phase.aligner_range_to}</strong> aligneurs
              <span className="ml-2 text-muted-foreground">
                ({phase.aligner_range_to - phase.aligner_range_from + 1} aligneurs)
              </span>
            </span>
          </div>
        </div>
      ))}

      {/* Total */}
      <div className="flex items-center justify-between px-4 py-3 bg-primary/5 border border-primary/20 rounded-lg">
        <span className="text-xs text-muted-foreground">
          Total si toutes les phases facturées
        </span>
        <span className="text-sm font-bold text-primary">
          {formatCurrency(phases.reduce((s, p) => s + parseFloat(p.phase_price || 0), 0))}
        </span>
      </div>
    </div>
  );
};

// ─── Generate Invoice Modal ───────────────────────────────────────────────────
const GenerateInvoiceModal = ({ isOpen, onClose, client, onSuccess }) => {
  const [formData, setFormData] = useState({ taxPercentage: 0, dueDate: '', notes: '', paymentMethod: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const defaultDue = new Date();
      defaultDue.setDate(defaultDue.getDate() + 30);
      setFormData({ taxPercentage: 0, dueDate: defaultDue.toISOString().split('T')[0], notes: '', paymentMethod: '' });
    }
  }, [isOpen]);

  const formatPrice = (price, currencyCode = 'EUR') =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currencyCode }).format(price);

  const pkg = client?.commercial_packages;
  const subtotal = pkg?.final_price || 0;
  const taxAmount = subtotal * (formData.taxPercentage / 100);
  const total = subtotal + taxAmount;
  const currencyCode = pkg?.countries?.currency_code || 'EUR';

  const generateInvoiceNumber = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const rand = Math.floor(Math.random() * 9000) + 1000;
    return `INV-${year}${month}-${rand}`;
  };

  const handleGenerate = async () => {
    if (!pkg) { toast.error('Ce client n\'a pas de package assigné'); return; }
    if (!formData.dueDate) { toast.error('Veuillez sélectionner une date d\'échéance'); return; }
    try {
      setSaving(true);
      const invoiceData = {
        invoice_number: generateInvoiceNumber(),
        client_name: client.full_name,
        client_email: client.email,
        package_id: client.package_id,
        client_id: client.id,
        country_id: client.countries?.id || null,
        status: 'draft',
        issue_date: new Date().toISOString().split('T')[0],
        due_date: formData.dueDate,
        subtotal,
        tax_percentage: formData.taxPercentage,
        tax_amount: taxAmount,
        total_amount: total,
        amount_paid: 0,
        balance_due: total,
        payment_method: formData.paymentMethod || null,
        notes: formData.notes || null,
      };
      const { data, error } = await supabase.from('invoices').insert(invoiceData).select().single();
      if (error) throw error;
      toast.success(`Facture ${data.invoice_number} créée avec succès !`);
      onSuccess();
      onClose();
    } catch (error) {
      toast.error('Erreur lors de la création de la facture', { description: error?.message });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
      <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col animate-slide-in">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Générer une facture</h2>
            <p className="text-sm text-muted-foreground mt-1">{client?.full_name}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors"><Icon name="X" size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {!pkg ? (
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
              <div className="flex items-center gap-3">
                <Icon name="AlertTriangle" size={20} className="text-destructive" />
                <p className="text-sm text-destructive">Ce client n'a pas de package assigné. Assignez un package avant de générer une facture.</p>
              </div>
            </div>
          ) : (
            <>
              <div className="bg-muted/40 border border-border rounded-lg p-4 space-y-2">
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2"><Icon name="Package" size={16} />Résumé du package</h4>
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Package</span><span className="font-medium text-foreground">{pkg.package_name}</span></div>
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Unités incluses</span><span className="font-medium text-foreground">{pkg.included_units}</span></div>
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Sous-total</span><span className="font-medium text-foreground">{formatPrice(subtotal, currencyCode)}</span></div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">TVA (%)</label>
                <input type="number" min="0" max="100" value={formData.taxPercentage}
                  onChange={(e) => setFormData(prev => ({ ...prev, taxPercentage: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Date d'échéance <span className="text-destructive">*</span></label>
                <input type="date" value={formData.dueDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Méthode de paiement</label>
                <select value={formData.paymentMethod}
                  onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value }))}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary">
                  <option value="">Sélectionner (optionnel)</option>
                  <option value="bank_transfer">Virement bancaire</option>
                  <option value="card">Carte bancaire</option>
                  <option value="cash">Espèces</option>
                  <option value="check">Chèque</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Notes</label>
                <textarea value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3} placeholder="Notes optionnelles..."
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none" />
              </div>
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Sous-total</span><span className="text-foreground">{formatPrice(subtotal, currencyCode)}</span></div>
                {formData.taxPercentage > 0 && (
                  <div className="flex justify-between text-sm"><span className="text-muted-foreground">TVA ({formData.taxPercentage}%)</span><span className="text-foreground">{formatPrice(taxAmount, currencyCode)}</span></div>
                )}
                <div className="border-t border-primary/20 pt-2 flex justify-between">
                  <span className="font-semibold text-foreground">Total</span>
                  <span className="font-bold text-lg text-primary">{formatPrice(total, currencyCode)}</span>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-3 p-6 border-t border-border">
          <Button variant="default" fullWidth onClick={handleGenerate} loading={saving} disabled={!pkg || saving} iconName="FileText" iconPosition="left" iconSize={18}>Générer la facture</Button>
          <Button variant="outline" fullWidth onClick={onClose} disabled={saving}>Annuler</Button>
        </div>
      </div>
    </div>
  );
};

// ─── Invoice Details Modal (read-only for dentist_client) ────────────────────
const InvoiceDetailsModal = ({ isOpen, onClose, invoice }) => {
  if (!isOpen || !invoice) return null;

  const formatPrice = (price, currencyCode = 'EUR') =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currencyCode }).format(parseFloat(price) || 0);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

  const statusConfig = {
    paid: { label: 'Payé', className: 'bg-success/10 text-success', icon: 'CheckCircle' },
    pending: { label: 'En attente', className: 'bg-warning/10 text-warning', icon: 'Clock' },
    draft: { label: 'Brouillon', className: 'bg-muted text-muted-foreground', icon: 'FileText' },
    sent: { label: 'Envoyé', className: 'bg-primary/10 text-primary', icon: 'Send' },
    partially_paid: { label: 'Partiel', className: 'bg-accent/10 text-accent', icon: 'PieChart' },
    overdue: { label: 'En retard', className: 'bg-destructive/10 text-destructive', icon: 'AlertCircle' },
    cancelled: { label: 'Annulé', className: 'bg-muted text-muted-foreground', icon: 'XCircle' },
  };
  const sc = statusConfig[invoice.status] || statusConfig.pending;
  const currency = invoice.currencyCode || 'EUR';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
      <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col animate-slide-in">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Détails de la facture</h2>
            <p className="text-sm text-muted-foreground mt-1">{invoice.invoiceNumber}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors"><Icon name="X" size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Status */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Statut</span>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${sc.className}`}>
              <Icon name={sc.icon} size={13} />{sc.label}
            </span>
          </div>

          {/* Dates */}
          <div className="bg-muted/40 border border-border rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Date d'émission</span><span className="font-medium text-foreground">{formatDate(invoice.issueDate)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Date d'échéance</span><span className="font-medium text-foreground">{formatDate(invoice.dueDate)}</span></div>
            {invoice.paymentDate && (
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Date de paiement</span><span className="font-medium text-success">{formatDate(invoice.paymentDate)}</span></div>
            )}
            {invoice.paymentMethod && (
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Méthode</span><span className="font-medium text-foreground capitalize">{invoice.paymentMethod.replace('_', ' ')}</span></div>
            )}
          </div>

          {/* Amounts */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Sous-total</span><span className="text-foreground">{formatPrice(invoice.subtotal, currency)}</span></div>
            {invoice.tax_percentage > 0 && (
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">TVA ({invoice.tax_percentage}%)</span><span className="text-foreground">{formatPrice(invoice.tax_amount, currency)}</span></div>
            )}
            <div className="border-t border-primary/20 pt-2 flex justify-between">
              <span className="font-semibold text-foreground">Total</span>
              <span className="font-bold text-lg text-primary">{formatPrice(invoice.totalAmount, currency)}</span>
            </div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Montant payé</span><span className="font-medium text-success">{formatPrice(invoice.amountPaid, currency)}</span></div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Reste dû</span>
              <span className={`font-medium ${parseFloat(invoice.balanceDue) > 0 ? 'text-destructive' : 'text-success'}`}>
                {formatPrice(invoice.balanceDue, currency)}
              </span>
            </div>
          </div>

          {invoice.notes && (
            <div className="bg-muted/30 rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-1">Notes</p>
              <p className="text-sm text-foreground">{invoice.notes}</p>
            </div>
          )}
        </div>
        <div className="p-6 border-t border-border">
          <Button variant="outline" fullWidth onClick={onClose}>Fermer</Button>
        </div>
      </div>
    </div>
  );
};

// ─── My Invoices Section (dentist_client view) ────────────────────────────────
const MyInvoicesSection = ({ currentUserId }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
const handleDownloadInvoicePdf = (inv) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const currencyCode = inv.currencyCode || 'EUR';
  const W = 210;
  const margin = 14;
  const contentW = W - margin * 2;

  const fmt = (val) => {
    const formatted = new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currencyCode,
    }).format(val ?? 0);
    return formatted
      .replace(/\u202f/g, ' ')
      .replace(/\u00a0/g, ' ');
  };

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' }) : '—';

  const indigo  = [79, 70, 229];
  const gray100 = [243, 244, 246];
  const gray400 = [156, 163, 175];
  const gray700 = [55, 65, 81];
  const green   = [22, 163, 74];
  const red     = [220, 38, 38];
  const white   = [255, 255, 255];

  let y = margin;

  const rect = (x, ry, w, h, color) => {
    doc.setFillColor(...color);
    doc.rect(x, ry, w, h, 'F');
  };

  const text = (str, x, ty, opts = {}) => {
    doc.setFontSize(opts.size || 10);
    doc.setFont('helvetica', opts.bold ? 'bold' : 'normal');
    doc.setTextColor(...(opts.color || gray700));
    doc.text(String(str ?? '—'), x, ty, { align: opts.align || 'left' });
  };

  const line = (ly, color = gray100) => {
    doc.setDrawColor(...color);
    doc.setLineWidth(0.3);
    doc.line(margin, ly, W - margin, ly);
  };

  // ── Header band ──
  rect(0, 0, W, 32, indigo);

  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...white);
  doc.text('ClearMotion', margin, 14);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(199, 210, 254);
  doc.text('Laboratoire dentaire orthodontique', margin, 20);

  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...white);
  doc.text('FACTURE', W - margin, 13, { align: 'right' });

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(199, 210, 254);
  doc.text(inv.number || inv.invoiceNumber || '—', W - margin, 20, { align: 'right' });

  const statusLabel = inv.status === 'paid' ? 'PAYÉE' : 'ENVOYÉE';
  const statusColor = inv.status === 'paid' ? [220, 252, 231] : [254, 249, 195];
  const statusText  = inv.status === 'paid' ? [22, 101, 52]   : [133, 77, 14];
  doc.setFillColor(...statusColor);
  doc.roundedRect(W - margin - 28, 23, 28, 6.5, 2, 2, 'F');
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...statusText);
  doc.text(statusLabel, W - margin - 14, 27.5, { align: 'center' });

  y = 40;

  // ── Dates row ──
  rect(margin, y, contentW, 14, gray100);
  const col = contentW / 3;
  const dateFields = [
    { label: "Date d'émission",  val: fmtDate(inv.rawIssueDate   || inv.issueDate) },
    { label: "Date d'échéance",  val: fmtDate(inv.rawDueDate     || inv.dueDate) },
    { label: 'Date de paiement', val: fmtDate(inv.rawPaymentDate || inv.paymentDate) },
  ];
  dateFields.forEach((f, i) => {
    const cx = margin + i * col + col / 2;
    text(f.label, cx, y + 5,  { size: 7.5, color: gray400, align: 'center' });
    text(f.val,   cx, y + 11, { size: 8.5, bold: true, color: gray700, align: 'center' });
  });

  y += 20;

  // ── Parties ──
  const halfW = (contentW - 6) / 2;

  rect(margin, y, halfW, 26, gray100);
  text('FACTURÉ À', margin + 4, y + 6, { size: 7.5, color: gray400, bold: true });
  text(inv.dentistName || inv.clientName || '—', margin + 4, y + 13, { size: 9, bold: true });
  text(inv.dentistEmail || inv.clientEmail || '—', margin + 4, y + 19, { size: 8, color: gray400 });

  const cx2 = margin + halfW + 6;
  rect(cx2, y, halfW, 26, gray100);
  text('INFORMATIONS DU CAS', cx2 + 4, y + 6, { size: 7.5, color: gray400, bold: true });
  text(`Cas : ${inv.caseNumber || '—'}`, cx2 + 4, y + 13, { size: 9, bold: true });
  text(`Patient : ${inv.patientName || '—'}`, cx2 + 4, y + 19, { size: 8, color: gray400 });

  y += 33;

  // ── Table header ──
  rect(margin, y, contentW, 9, indigo);
  const cols = [
    { label: 'DESCRIPTION', x: margin + 3,         align: 'left'   },
    { label: 'ALIGNEURS',   x: margin + 85 + 12.5,  align: 'center' },
    { label: 'PLAGE',       x: margin + 112 + 15,   align: 'center' },
    { label: 'MONTANT HT',  x: W - margin - 3,      align: 'right'  },
  ];
  cols.forEach((c) => {
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...white);
    doc.text(c.label, c.x, y + 6, { align: c.align });
  });
  y += 9;

  // ── Table row ──
  rect(margin, y, contentW, 18, [249, 250, 251]);
  line(y, [229, 231, 235]);

  const lotLabel = inv.lotNumber || (inv.phaseNumber ? `LOT ${inv.phaseNumber}` : inv.invoiceType || 'Package');
  text(lotLabel, margin + 3, y + 7, { size: 9, bold: true });
  text(`${inv.dentistName || inv.clientName || '—'}`, margin + 3, y + 13, { size: 7.5, color: gray400 });

  text(String(inv.alignersCount ?? '—'), margin + 85 + 12.5, y + 10, { size: 9, align: 'center' });

  const rangeLabel = inv.alignerRangeFrom && inv.alignerRangeTo
    ? `${inv.alignerRangeFrom}–${inv.alignerRangeTo}`
    : '—';
  text(rangeLabel, margin + 112 + 15, y + 10, { size: 9, align: 'center' });

  text(fmt(inv.rawSubtotal ?? Number(inv.subtotal ?? 0)), W - margin - 3, y + 10, { size: 9, bold: true, align: 'right' });

  line(y + 18, [229, 231, 235]);
  y += 26;

  // ── Totals ──
  const totalsX      = W - margin - 60;
  const totalsLabelX = totalsX + 2;
  const totalsValX   = W - margin - 2;

  const discountAmount = parseFloat(inv.discountAmount ?? inv.rawDiscount ?? inv.discount_amount ?? 0);
  const taxAmount      = parseFloat(inv.taxAmount ?? inv.rawTax ?? inv.tax_amount ?? 0);
  const rawSubtotal    = inv.rawSubtotal   ?? Number(inv.subtotal     ?? 0);
  const rawTotal       = inv.rawTotal      ?? Number(inv.totalAmount  ?? inv.total_amount  ?? 0);
  const rawAmountPaid  = inv.rawAmountPaid ?? Number(inv.amountPaid   ?? inv.amount_paid   ?? 0);
  const balanceDueNum  = parseFloat(inv.balanceDueRaw ?? inv.rawBalanceDue ?? inv.balanceDue ?? inv.balance_due ?? 0);
  const taxPct         = inv.taxPercentage ?? inv.tax_percentage ?? 0;

  const totalRows = [
    { label: 'Sous-total', val: fmt(rawSubtotal), color: gray700 },
    ...(discountAmount > 0
      ? [{ label: 'Remise', val: `-${fmt(discountAmount)}`, color: green }]
      : []),
    ...(taxAmount > 0
      ? [{ label: `TVA (${taxPct}%)`, val: fmt(taxAmount), color: gray700 }]
      : []),
  ];

  totalRows.forEach((row) => {
    text(row.label, totalsLabelX, y + 5, { size: 8, color: gray400 });
    text(row.val,   totalsValX,   y + 5, { size: 8, bold: true, color: row.color, align: 'right' });
    line(y + 7);
    y += 9;
  });

  rect(totalsX, y, 60, 12, indigo);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...white);
  doc.text('TOTAL', totalsLabelX + 2, y + 8);
  doc.text(fmt(rawTotal), totalsValX - 2, y + 8, { align: 'right' });
  y += 17;

  text('Montant payé', totalsLabelX, y + 5, { size: 8.5, color: gray400 });
  text(fmt(rawAmountPaid), totalsValX, y + 5, { size: 8.5, bold: true, color: green, align: 'right' });
  line(y + 7);
  y += 9;

  const balanceColor = balanceDueNum > 0 ? red : green;
  text('Solde dû', totalsLabelX, y + 5, { size: 8.5, bold: true, color: gray700 });
  text(fmt(balanceDueNum), totalsValX, y + 5, { size: 8, bold: true, color: balanceColor, align: 'right' });
  y += 14;

  // ── Payment method ──
  if (inv.paymentMethod && inv.paymentMethod !== '—') {
    rect(margin, y, contentW, 10, gray100);
    text('Méthode de paiement :', margin + 4, y + 7, { size: 8, color: gray400 });
    text(inv.paymentMethod, margin + 50, y + 7, { size: 8, bold: true });
    y += 15;
  }

  // ── Notes ──
  if (inv.notes && inv.notes !== '—') {
    text('Notes :', margin, y + 5, { size: 8, bold: true, color: gray400 });
    y += 8;
    const noteLines = doc.splitTextToSize(inv.notes, contentW);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...gray700);
    doc.text(noteLines, margin, y);
    y += noteLines.length * 5 + 6;
  }

  // ── Footer band ──
  const footerY = 285;
  rect(0, footerY, W, 12, indigo);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(199, 210, 254);
  doc.text(`Générée le ${new Date().toLocaleDateString('fr-FR')}`, margin, footerY + 7);
  doc.text(inv.number || inv.invoiceNumber || '—', W / 2, footerY + 7, { align: 'center' });
  doc.text('ClearMotion', W - margin, footerY + 7, { align: 'right' });

  doc.save(`Facture_${inv.number || inv.invoiceNumber}.pdf`);
};
  useEffect(() => {
    if (currentUserId) fetchMyInvoices();
  }, [currentUserId]);

  const fetchMyInvoices = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('invoices')
.select(`
          id, invoice_number, client_name, client_email, status,
          issue_date, due_date, payment_date, subtotal,
          discount_amount, tax_percentage, tax_amount, total_amount,
          amount_paid, balance_due, payment_method, notes, invoice_type,
          countries ( currency_code )
        `)
        .eq('client_id', currentUserId)
        .order('created_at', { ascending: false });
          
      if (error) throw error;
setInvoices((data || []).map(inv => {
  const curr        = inv.countries?.currency_code || 'EUR';
  const rawSubtotal   = Number(inv.subtotal        ?? 0);
  const rawTotal      = Number(inv.total_amount    ?? 0);
  const rawAmountPaid = Number(inv.amount_paid     ?? 0);
  const rawBalanceDue = Number(inv.balance_due     ?? 0);
  const rawDiscount   = Number(inv.discount_amount ?? 0);
  const rawTax        = Number(inv.tax_amount      ?? 0);
  const taxPct        = inv.tax_percentage         ?? 0;

  const invoiceTypeLabel =
    inv.invoice_type === 'design_fee' ? 'Design Frais' :
    inv.invoice_type === 'phase'      ? 'Phase'        :
    inv.invoice_type === 'package'    ? 'Package'      :
    'Package';

  return {
    // ── spread raw DB row first ──
    ...inv,

    // ── UI display fields ──
    invoiceNumber:  inv.invoice_number,
    number:         inv.invoice_number,
    clientName:     inv.client_name  || '—',
    clientEmail:    inv.client_email || '—',
    issueDate:      inv.issue_date,
    dueDate:        inv.due_date,
    paymentDate:    inv.payment_date,
    totalAmount:    inv.total_amount,
    amountPaid:     inv.amount_paid,
    balanceDue:     inv.balance_due,
    paymentMethod:  inv.payment_method || null,
    notes:          inv.notes || null,
    currencyCode:   curr,
    invoiceType:    invoiceTypeLabel,

    // ── PDF numeric fields ──
    rawSubtotal,
    rawTotal,
    rawAmountPaid,
    rawBalanceDue,
    rawDiscount,
    rawTax,
    taxPercentage:  taxPct,

    // ── PDF date fields (single definition) ──
    rawIssueDate:   inv.issue_date,
    rawDueDate:     inv.due_date,
    rawPaymentDate: inv.payment_date,

    // ── PDF identity fields (doc 10 names) ──
    dentistName:      inv.client_name  || '—',
    dentistEmail:     inv.client_email || '—',
    caseNumber:       '—',
    patientName:      '—',
    phaseNumber:      null,
    lotNumber:        invoiceTypeLabel,
    phaseName:        invoiceTypeLabel,
    alignersCount:    null,
    alignerRangeFrom: null,
    alignerRangeTo:   null,
    discountAmount:   rawDiscount,
    taxAmount:        rawTax,
    balanceDueRaw:    rawBalanceDue,
  };
}));
    } catch (err) {
      console.error('Error fetching invoices:', err);
      toast.error('Erreur lors du chargement de vos factures');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price, currencyCode = 'EUR') =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currencyCode }).format(parseFloat(price) || 0);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

 const statusConfig = {
      paid: {
        label: 'Payé',
        className: 'bg-success/10 text-success',
        icon: 'CheckCircle',
      },
      pending: {
        label: 'Envoyée',
        className: 'bg-warning/10 text-warning',
        icon: 'Clock',
      },
      draft: {
        label: 'Envoyée',
      className: 'bg-warning/10 text-warning',
        icon: 'FileText',
      },
      sent: {
        label: 'Envoyé',
      className: 'bg-warning/10 text-warning',
        icon: 'Send',
      },
      partially_paid: {
        label: 'Partiel',
        className: 'bg-accent/10 text-accent',
        icon: 'PieChart',
      },
      overdue: {
        label: 'En retard',
        className: 'bg-error/10 text-error',
        icon: 'AlertCircle',
      },
      cancelled: {
        label: 'Annulé',
        className: 'bg-muted text-muted-foreground',
        icon: 'XCircle',
      },
    };
  // Summary stats
  const totalDue = invoices.reduce((s, i) => s + parseFloat(i.balanceDue || 0), 0);
  const totalPaid = invoices.reduce((s, i) => s + parseFloat(i.amountPaid || 0), 0);
  const overdueCount = invoices.filter(i => i.status === 'overdue').length;
  const currency = invoices[0]?.currencyCode || 'EUR';

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
            <Icon name="FileText" size={20} className="text-primary" />
            Mes factures
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">Historique de vos factures et paiements</p>
        </div>
        <button
          onClick={fetchMyInvoices}
          className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
          title="Actualiser"
        >
          <Icon name="RefreshCw" size={16} />
        </button>
      </div>

      {/* Summary cards */}
      {invoices.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs text-muted-foreground mb-1">Total factures</p>
            <p className="text-2xl font-bold text-foreground">{invoices.length}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs text-muted-foreground mb-1">Montant payé</p>
            <p className="text-2xl font-bold text-success">{formatPrice(totalPaid, currency)}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs text-muted-foreground mb-1">Reste à payer</p>
            <p className={`text-2xl font-bold ${totalDue > 0 ? 'text-destructive' : 'text-success'}`}>
              {formatPrice(totalDue, currency)}
            </p>
            {overdueCount > 0 && (
              <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                <Icon name="AlertCircle" size={12} />{overdueCount} en retard
              </p>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12 bg-card border border-border rounded-lg">
          <Icon name="Loader" size={24} className="animate-spin text-primary mr-3" />
          <span className="text-muted-foreground">Chargement des factures...</span>
        </div>
      ) : invoices.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-card border border-border rounded-lg">
          <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-4">
            <Icon name="FileText" size={28} className="text-muted-foreground" />
          </div>
          <h3 className="text-base font-semibold text-foreground mb-1">Aucune facture</h3>
          <p className="text-sm text-muted-foreground text-center max-w-xs">
            Vous n'avez pas encore de factures. Elles apparaîtront ici une fois générées.
          </p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">N° Facture</th>
                                        <th className="px-5 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">Type</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">Émise le</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">Détails</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">Total</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">Payé</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">Reste dû</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider">Statut</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {invoices.map((inv) => {
                    const sc = statusConfig[inv.status] || statusConfig.pending;
                    const curr = inv.currencyCode || 'EUR';
                    const isOverdue = inv.status === 'overdue' || (inv.dueDate && inv.dueDate < new Date().toISOString().split('T')[0] && inv.status !== 'paid');
                    return (
                      <tr key={inv.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-5 py-4">
                          <span className="text-sm font-medium text-foreground">{inv.invoiceNumber}</span>
                        </td>
                          <td className="px-5 py-4">
                          <span className="text-sm font-medium text-foreground">{inv.invoiceType || 'Phase'}</span>
                        </td>
                        <td className="px-5 py-4">
                          <span className="text-sm text-muted-foreground">{formatDate(inv.issueDate)}</span>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`text-sm ${isOverdue ? 'text-destructive font-medium' : 'text-foreground'}`}>
                            {inv.notes}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <span className="text-sm font-semibold text-foreground">{formatPrice(inv.totalAmount, curr)}</span>
                        </td>
                        <td className="px-5 py-4">
                          <span className="text-sm text-success font-medium">{formatPrice(inv.amountPaid, curr)}</span>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`text-sm font-medium ${parseFloat(inv.balanceDue) > 0 ? 'text-destructive' : 'text-success'}`}>
                            {formatPrice(inv.balanceDue, curr)}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${sc.className}`}>
                            <Icon name={sc.icon} size={12} />{sc.label}
                          </span>
                        </td>
  
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {invoices.map((inv) => {
              const sc = statusConfig[inv.status] || statusConfig.pending;
              const curr = inv.currencyCode || 'EUR';
              return (
                <div key={inv.id} className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-sm font-semibold text-foreground">{inv.invoiceNumber}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Émise le {formatDate(inv.issueDate)}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${sc.className}`}>
                      <Icon name={sc.icon} size={11} />{sc.label}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                    <div className="bg-muted/40 rounded p-2">
                      <p className="text-xs text-muted-foreground">Total</p>
                      <p className="text-sm font-bold text-foreground">{formatPrice(inv.totalAmount, curr)}</p>
                    </div>
                    <div className="bg-muted/40 rounded p-2">
                      <p className="text-xs text-muted-foreground">Payé</p>
                      <p className="text-sm font-bold text-success">{formatPrice(inv.amountPaid, curr)}</p>
                    </div>
                    <div className="bg-muted/40 rounded p-2">
                      <p className="text-xs text-muted-foreground">Reste</p>
                      <p className={`text-sm font-bold ${parseFloat(inv.balanceDue) > 0 ? 'text-destructive' : 'text-success'}`}>
                        {formatPrice(inv.balanceDue, curr)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => { setSelectedInvoice(inv); setShowDetails(true); }}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-muted hover:bg-muted/80 text-foreground border border-border transition-colors"
                  >
                    <Icon name="Eye" size={14} />Voir les détails
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}

      <InvoiceDetailsModal
        isOpen={showDetails}
        onClose={() => { setShowDetails(false); setSelectedInvoice(null); }}
        invoice={selectedInvoice}
      />
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const PackUsers = () => {
  const { isCollapsed, toggleSidebar } = useSidebar();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userCountryId, setUserCountryId] = useState(null);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [dentistClients, setDentistClients] = useState([]);

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
    const [expandedPackageIds, setExpandedPackageIds] = useState(new Set()); // ← HERE


  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
    { label: 'Packs disponibles', path: '/pack-users' }
  ];

  useEffect(() => { fetchCurrentUserCountry(); }, []);
  useEffect(() => { if (isAdmin) fetchDentistClients(); }, [isAdmin]);

  const fetchCurrentUserCountry = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!user) { setError('Aucun utilisateur authentifié trouvé'); setLoading(false); return; }

      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('country_id')
        .eq('id', user.id)
        .single();
      if (profileError) throw profileError;

      const { data: userRoleAssignment, error: roleError } = await supabase
        .from('user_role_assignments')
        .select(`user_roles ( role_type )`)
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();

      const roleType = userRoleAssignment?.user_roles?.role_type;

      if (!roleError && roleType === 'administrator') {
        setIsAdmin(true);
        setLoading(false);
        return;
      }

      const countryId = profile?.country_id ?? null;
      setUserCountryId(countryId);
      await loadPackagesForCountry(countryId);
    } catch (error) {
      console.error('Error fetching user country:', error);
      setError(error?.message || 'Échec de la récupération des informations utilisateur');
      setLoading(false);
    }
  };

  const fetchDentistClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: clientsError } = await supabase
        .from('user_profiles')
        .select(`
          id, full_name, email, package_id, case_number, current_case_number,
          is_active, created_at,
          countries ( id, country_name, country_code ),
          commercial_packages ( id, package_name, package_code, tier, final_price, included_units, countries ( currency_code ) )
        `)
        .order('created_at', { ascending: false });
      if (clientsError) throw clientsError;

      const filteredClients = [];
      for (const profile of data || []) {
        const { data: roleData, error: roleError } = await supabase
          .from('user_role_assignments')
          .select(`user_roles ( role_type )`)
          .eq('user_id', profile.id)
          .eq('is_active', true)
          .single();
        if (!roleError && roleData?.user_roles?.role_type === 'dentist_client') {
          filteredClients.push(profile);
        }
      }
      setDentistClients(filteredClients);
    } catch (error) {
      console.error('Error fetching dentist clients:', error);
      setError(error?.message || 'Échec de la récupération des clients dentistes');
    } finally {
      setLoading(false);
    }
  };

  /** Filtre par pays si défini ; sinon tous les packs actifs (ex. dentiste sans pays). */
  const loadPackagesForCountry = async (countryId) => {
    try {
      setLoading(true);
      setError(null);
      let query = supabase
        .from('commercial_packages')
        .select(`
          *,
          countries ( id, country_name, country_code, currency_code ),
          package_services ( id, quantity, service:services ( id, service_name, service_code, description, category, unit_price ) )
        `)
        .eq('status', 'active')
        .order('tier', { ascending: true })
        .order('base_price', { ascending: true });
      if (countryId) {
        query = query.eq('country_id', countryId);
      }
      const { data, error: packagesError } = await query;
      if (packagesError) throw packagesError;
      const pkgs = data || [];
      setPackages(pkgs);
      setExpandedPackageIds(new Set(pkgs.map((p) => p.id)));
    } catch (error) {
      console.error('Error fetching packages:', error);
      setError(error?.message || 'Échec de la récupération des packages');
    } finally {
      setLoading(false);
    }
  };

  const fetchPackages = async () => {
    await loadPackagesForCountry(userCountryId);
  };

  const openAssignModal = (client) => { setSelectedClient(client); setAssignModalOpen(true); };
  const openInvoiceModal = (client) => { setSelectedClient(client); setInvoiceModalOpen(true); };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'bronze': return 'bg-amber-700 text-white';
      case 'silver': return 'bg-gray-400 text-gray-900';
      case 'gold': return 'bg-yellow-500 text-gray-900';
      case 'platinum': return 'bg-purple-600 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getCategoryIcon = (category) => {
    const icons = { orthodontics: 'Stethoscope', aligners: 'Smile', consultation: 'UserCheck', imaging: 'Camera', treatment: 'Heart', maintenance: 'Wrench' };
    return icons[category] || 'Package';
  };

  const formatPrice = (price, currencyCode = 'EUR') =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currencyCode }).format(price);

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });

  // ── Admin: Dentist Clients Table ──────────────────────────────────────────
  const renderDentistClientsView = () => (
    <div className="space-y-4">
      {dentistClients.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <Icon name="Users" size={32} className="text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Aucun client dentiste</h3>
          <p className="text-sm text-muted-foreground text-center max-w-md">Il n'y a actuellement aucun client dentiste enregistré dans le système.</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Client</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Pays</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Package</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Cas disponibles</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Statut</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">Date d'inscription</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {dentistClients.map((client) => (
                  <tr key={client.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-foreground">{client.full_name}</p>
                        <p className="text-sm text-muted-foreground">{client.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-foreground">{client.countries?.country_name || 'N/A'}</span>
                        <span className="text-xs text-muted-foreground">({client.countries?.country_code})</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {client.commercial_packages ? (
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-foreground">{client.commercial_packages.package_name}</span>
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getTierColor(client.commercial_packages.tier)}`}>{client.commercial_packages.tier}</span>
                          </div>
                          <p className="text-xs text-muted-foreground">{client.commercial_packages.package_code}</p>
                          <p className="text-sm font-semibold text-primary mt-1">
                            {formatPrice(client.commercial_packages.final_price, client.commercial_packages.countries?.currency_code)}
                          </p>
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-sm text-amber-600 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full">
                          <Icon name="AlertCircle" size={13} />Aucun package
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {client.commercial_packages ? (
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-semibold text-foreground">{client.current_case_number || 0} / {client.case_number || 0}</span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-2">
                              <div className="bg-primary h-2 rounded-full transition-all"
                                style={{ width: `${((client.current_case_number || 0) / (client.case_number || 1)) * 100}%` }} />
                            </div>
                          </div>
                        </div>
                      ) : <span className="text-sm text-muted-foreground">-</span>}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${client.is_active ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive'}`}>
                        <Icon name={client.is_active ? 'CheckCircle2' : 'XCircle'} size={12} />
                        {client.is_active ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-muted-foreground">{formatDate(client.created_at)}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => openAssignModal(client)}
                          title={client.commercial_packages ? 'Modifier le package' : 'Assigner un package'}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            client.commercial_packages ? 'bg-muted hover:bg-muted/80 text-foreground border border-border' : 'bg-amber-500 hover:bg-amber-600 text-white'
                          }`}>
                          <Icon name={client.commercial_packages ? 'Pencil' : 'PackagePlus'} size={13} />
                          {client.commercial_packages ? 'Modifier' : 'Assigner'}
                        </button>
                        <button onClick={() => openInvoiceModal(client)} title="Générer une facture"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-primary hover:bg-primary/90 text-primary-foreground transition-colors">
                          <Icon name="FileText" size={13} />Facture
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
// ─── Billing Phases Read-Only View ───────────────────────────────────────────

const renderPackagesView = () => (
  <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
    {packages.length === 0 ? (
      <div className="col-span-full flex flex-col items-center justify-center py-16 px-4">
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
          <Icon name="Package" size={32} className="text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">Aucun package disponible</h3>
        <p className="text-sm text-muted-foreground text-center max-w-md">
          Il n'y a actuellement aucun package commercial disponible dans votre pays.
        </p>
      </div>
    ) : (
      packages.map((pkg) => {
        const currencyCode = pkg?.countries?.currency_code || 'EUR';
        const isExpanded = expandedPackageIds.has(pkg.id);

        return (
          <div
            key={pkg.id}
            className="bg-card border-2 border-border rounded-lg overflow-hidden hover:shadow-lg transition-all duration-200"
          >
            {/* ── Package Header ── */}
            <div className="p-6 border-b border-border">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-foreground mb-1">{pkg.package_name}</h3>
                  <p className="text-sm text-muted-foreground">{pkg.package_code}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${getTierColor(pkg.tier)}`}>
                  {pkg.tier}
                </span>
              </div>
              {pkg.description && (
                <p className="text-sm text-muted-foreground">{pkg.description}</p>
              )}
            </div>

            {/* ── Pricing ── */}
            <div className="p-6 border-b border-border bg-muted/30">
              <div className="flex items-baseline gap-2 mb-2">
                {pkg.discount_percentage > 0 && (
                  <span className="text-lg text-muted-foreground line-through">
                    {formatPrice(pkg.base_price, currencyCode)}
                  </span>
                )}
                <span className="text-3xl font-bold text-foreground">
                  {formatPrice(pkg.final_price, currencyCode)}
                </span>
              </div>
              {pkg.discount_percentage > 0 && (
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-green-500 text-white text-xs font-semibold px-2 py-1 rounded">
                    -{pkg.discount_percentage}%
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Économisez {formatPrice(pkg.base_price - pkg.final_price, currencyCode)}
                  </span>
                </div>
              )}
              <div className="space-y-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Icon name="Package" size={16} className="text-primary" />
                  <span className="font-medium">{pkg.included_units} unités incluses</span>
                </div>
                {pkg.additional_unit_price && (
                  <div className="flex items-center gap-2">
                    <Icon name="Plus" size={16} className="text-primary" />
                    <span>Unités supp. : {formatPrice(pkg.additional_unit_price, currencyCode)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* ── Included Services ── */}
            {pkg.package_services && pkg.package_services.length > 0 && (
              <div className="p-6 border-b border-border">
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="ListChecks" size={16} />
                  Services inclus ({pkg.package_services.length})
                </h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {pkg.package_services.map((ps) => (
                    <div key={ps.id} className="flex items-start gap-2 p-2 bg-muted/30 rounded-lg">
                      <Icon
                        name={getCategoryIcon(ps.service?.category)}
                        size={16}
                        className="text-primary mt-0.5 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {ps.service?.service_name}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-muted-foreground">{ps.service?.service_code}</span>
                          {ps.quantity > 1 && (
                            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                              × {ps.quantity}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="text-xs font-semibold text-primary flex-shrink-0">
                        {formatPrice(ps.service?.unit_price, currencyCode)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Features ── */}
            {pkg.features && pkg.features.length > 0 && (
              <div className="p-6 border-b border-border">
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="Sparkles" size={16} />
                  Avantages
                </h4>
                <ul className="space-y-2">
                  {pkg.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* ── Billing Phases Toggle (read-only) ── */}
            <div className="p-4 border-t border-border">
              <button
           onClick={() => setExpandedPackageIds(prev => {
  const next = new Set(prev);
  isExpanded ? next.delete(pkg.id) : next.add(pkg.id);
  return next;
})}
                className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg bg-muted/40 hover:bg-muted/70 text-foreground border border-border transition-colors"
              >
                <span className="flex items-center gap-2 text-sm font-medium">
                  <Icon name="Layers" size={16} className="text-primary" />
                  Phases de facturation
                </span>
                <Icon
                  name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                  size={16}
                  className="text-muted-foreground"
                />
              </button>

              {/* ── Expanded Read-Only Phases Panel ── */}
              {isExpanded && (
                <BillingPhasesReadOnly
                  packageId={pkg.id}
                  currencyCode={currencyCode}
                />
              )}
            </div>

          </div>
        );
      })
    )}
  </div>
);

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />

      <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Breadcrumb items={breadcrumbItems} />

        <div className="px-6 py-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
            <div>
             
              <p className="text-muted-foreground mt-2">
                {isAdmin
                  ? 'Consultez, assignez et gérez les packages de vos clients dentistes':""}
              </p>
              
            </div>
            <div className="flex items-center gap-3">
              {isAdmin && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-muted border border-border rounded-lg">
                  <Icon name="Users" size={16} className="text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">{dentistClients.length} client{dentistClients.length !== 1 ? 's' : ''}</span>
                </div>
              )}
              <Button
                variant="outline"
                onClick={isAdmin ? fetchDentistClients : fetchPackages}
                iconName="RefreshCw"
                iconPosition="left"
                iconSize={18}
                disabled={loading}
              >
                Actualiser
              </Button>
            </div>
          </div>

          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-3">
                <Icon name="AlertCircle" size={20} className="text-destructive" />
                <div>
                  <h3 className="font-semibold text-destructive">Erreur</h3>
                  <p className="text-sm text-destructive/80">{error}</p>
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="flex items-center gap-3">
                <Icon name="Loader" size={24} className="animate-spin text-primary" />
                <span className="text-muted-foreground">{isAdmin ? 'Chargement des clients...' : 'Chargement des packages...'}</span>
              </div>
            </div>
          ) : !error ? (
            <>
              {isAdmin ? renderDentistClientsView() : renderPackagesView()}
            </>
          ) : null}
        </div>
      </main>

      <AssignPackageModal
        isOpen={assignModalOpen}
        onClose={() => { setAssignModalOpen(false); setSelectedClient(null); }}
        client={selectedClient}
        onSuccess={fetchDentistClients}
      />

      <GenerateInvoiceModal
        isOpen={invoiceModalOpen}
        onClose={() => { setInvoiceModalOpen(false); setSelectedClient(null); }}
        client={selectedClient}
        onSuccess={fetchDentistClients}
      />
    </div>
  );
};

export default PackUsers;